// src/AccessInfoModal.js
// Окно с данными для входа, которое администратор пересылает сотруднику.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AccessInfoModal.css';

// Без похожих друг на друга символов (0/O, 1/l/I): пароль часто
// переписывают с экрана или диктуют по телефону
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const DIGITS = '23456789';

const randomIndex = (max) => {
  // Отбрасываем значения из «хвоста», иначе первые символы алфавита
  // выпадали бы чаще остальных
  const limit = Math.floor(0x100000000 / max) * max;
  const buffer = new Uint32Array(1);
  do {
    window.crypto.getRandomValues(buffer);
  } while (buffer[0] >= limit);
  return buffer[0] % max;
};

const pick = (alphabet) => alphabet[randomIndex(alphabet.length)];

// 8 символов, как у выданных ранее учётных записей; сервер требует не меньше 8
export const generatePassword = (length = 8) => {
  const all = UPPER + LOWER + DIGITS;
  const chars = [pick(UPPER), pick(LOWER), pick(DIGITS)];
  while (chars.length < length) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

export const buildAccessText = ({ fullName, username, password, loginUrl }) => [
  'Доступ к «Электронной учительской»',
  '',
  `ФИО: ${fullName}`,
  `Логин: ${username}`,
  `Пароль: ${password}`,
  `Ссылка для входа: ${loginUrl}`,
].join('\n');

export default function AccessInfoModal({ text, onClose }) {
  const textRef = useRef(null);
  const [copyState, setCopyState] = useState('idle'); // idle | copied | failed

  useEffect(() => {
    // Перехватываем Escape раньше админ-панели: она слушает document
    // и закрылась бы вместе с окном
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    if (copyState === 'idle') return undefined;
    const timer = setTimeout(() => setCopyState('idle'), 2500);
    return () => clearTimeout(timer);
  }, [copyState]);

  // Выделяет весь текст блока — и для запасного копирования, и чтобы
  // его можно было скопировать вручную
  const selectText = () => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(textRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const copyWithSelection = () => {
    selectText();
    return document.execCommand('copy');
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else if (!copyWithSelection()) {
        throw new Error('copy failed');
      }
      setCopyState('copied');
    } catch (err) {
      // Буфер обмена бывает запрещён браузером — тогда хотя бы выделяем
      // текст, чтобы его можно было скопировать вручную
      try {
        if (copyWithSelection()) {
          setCopyState('copied');
          return;
        }
      } catch (ignored) {
        // выделение ниже всё равно оставляем
      }
      selectText();
      setCopyState('failed');
    }
  };

  // Портал в body: иначе окно оказалось бы внутри прокручиваемой
  // области админ-панели и под её оверлеем
  return createPortal(
    <div className="access-info" role="dialog" aria-modal="true" aria-labelledby="access-info-title">
      <div className="access-info-card">
        <div className="access-info-header">
          <h3 id="access-info-title">Доступ выдан</h3>
          <button type="button" className="access-info-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <p className="access-info-hint">
          Новый пароль уже сохранён. Он показывается только сейчас — скопируйте
          текст и отправьте его сотруднику.
        </p>

        {/* Обычный блок, а не поле ввода: текст нельзя изменить, но можно
            выделить, и он не обрастает полосами прокрутки */}
        <pre
          ref={textRef}
          className="access-info-text"
          tabIndex={0}
          aria-label="Данные для входа"
        >
          {text}
        </pre>

        <div className="access-info-actions">
          <span className={`access-info-status ${copyState}`} role="status" aria-live="polite">
            {copyState === 'copied' && 'Скопировано'}
            {copyState === 'failed' && 'Не удалось скопировать — текст выделен, нажмите Ctrl+C'}
          </span>
          <button type="button" className="admin-button admin-button-cancel" onClick={onClose}>
            Закрыть
          </button>
          <button type="button" className="admin-button admin-button-save" onClick={handleCopy}>
            {copyState === 'copied' ? 'Скопировано' : 'Скопировать'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
